import { getServerSession } from 'next-auth/next';
import { getSession as getClientSession, useSession as useClientSession } from 'next-auth/react';

import { authOptions } from '@/lib/auth';

/**
 * SessionManager
 *
 * Provides a unified API for obtaining the current NextAuth session
 * from both server and client contexts.  Server code should call
 * `getSession()`; client components can either call the same function
 * (it will fall back to `getSession()` from react) or use the
 * `useSession` hook exported here.
 */
export type NextAuthSession = Awaited<ReturnType<typeof getServerSession>>;

/**
 * Return the active session.  If executed on the server this wraps
 * `getServerSession`; in the browser it delegates to the client helper
 * from `next-auth/react`.
 */
export async function getSession(): Promise<NextAuthSession | null> {
  if (typeof window === 'undefined') {
    // server-side (SSR/SSG/edge).  cast to any because authOptions is typed loosely
    return await getServerSession(authOptions as any);
  }

  // client-side: returns a promise that resolves when the session has
  // been fetched by the auth client.  May return null if there is none.
  return await getClientSession();
}

/**
 * Hook for client components.  This is just a thin wrapper around
 * `useSession` from next-auth so that callers don't need to import
 * from two different packages.
 */
export function useSession() {
  return useClientSession();
}

// helper object for convenience
export const SessionManager = {
  getSession,
  useSession,
};
